import isEmpty from "lodash/isEmpty";

// Shape returned by formatProfileFromRemote. Consumed by dashboard.js,
// advanced_supplier_import.js (formattedProfile state seed), and out-of-scope
// channel_settings.js. The remote profile shape is non-trivial; treat unknowns
// as `unknown` to avoid over-specifying without losing call-site safety.
export interface RemoteProfile {
    Id?: unknown;
    Shared?: unknown;
    Name?: string;
    Description?: string;
    DocumentType?: string;
    Partner?: unknown;
    Category?: unknown;
    CategoryMappings?: unknown;
    AttributeMappings?: unknown;
    ListMappings?: unknown;
    AttributeMappingRules?: unknown[];
    Filter?: string;
    Fields?: Array<{ TargetField?: string; SourceField?: string | null } & Record<string, unknown>>;
    Rules?: Array<{
        TargetField?: string;
        SourceField?: string;
        Type?: string;
        Condition?: { Value?: string } & Record<string, unknown>;
    } & Record<string, unknown>>;
    [key: string]: unknown;
}

export const unescapeField = (field: unknown): unknown => {
    if (typeof field === "string" && field.startsWith("{") && field.endsWith("}")) {
        return field.slice(1, -1);
    }
    return field;
};

export function unescapeFieldsInCondition(condition: any): any {
    if (!condition) return condition;
    if (condition.Conditions && Array.isArray(condition.Conditions)) {
        return {
            ...condition,
            Conditions: condition.Conditions.map(unescapeFieldsInCondition)
        };
    } else {
        return {
            ...condition,
            Field: unescapeField(condition.Field)
        };
    }
}

export const formatProfileFromRemote = (remoteProfile: RemoteProfile): RemoteProfile => {
    let filterStringStored: any = {};
    //region Backwards compatibility with old profiles
    let parsedFilter = remoteProfile.Filter;
    if (
        parsedFilter !== undefined && parsedFilter !== ""
        && (remoteProfile.Fields === undefined || remoteProfile.Fields.length === 0)
        && (remoteProfile.Rules === undefined || remoteProfile.Rules.length === 0)
    ) {
        const parsed = JSON.parse(parsedFilter);
        filterStringStored = {
            Name: remoteProfile.Name,
            Description: remoteProfile.Description,
            DocumentType: remoteProfile.DocumentType,
            Partner: parsed.Partner,
            Category: parsed.Category,
            CategoryMappings: parsed.CategoryMappings,
            AttributeMappings: parsed.AttributeMappings,
            ListMappings: parsed.ListMappings
        }
    }
    //endregion
    let partner = remoteProfile.Partner;
    let categoryColFromFile: any = "";
    if (remoteProfile.Fields) {
        categoryColFromFile = remoteProfile.Fields.find(col => col.TargetField === "category");
        if(!categoryColFromFile) categoryColFromFile = "";
        if(categoryColFromFile.SourceField === null) categoryColFromFile = "";
        // Remove braces from escaping process
        if (!isEmpty(categoryColFromFile))
            categoryColFromFile = categoryColFromFile.SourceField.replace(/{|}/g, "");
    }
    let attributeMappings: any = [];
    if (remoteProfile.Fields) {
        attributeMappings = remoteProfile.Fields.reduce((acc: any, col) => {
            let targetField = col.TargetField;
            let sourceField = col.SourceField;
            if(!targetField || !sourceField) return acc;
            // Remove braces from escaping process
            sourceField = sourceField.replace("{", "").replace("}", "");
            return {
                ...acc,
                [targetField]: sourceField
            }
        }, {});
    }
    let categoryMappings: any = [];
    if (remoteProfile.Rules) {
        categoryMappings = remoteProfile.Rules.filter(rule => rule.TargetField === categoryColFromFile).reduce((acc: any, rule) => {
            let source: any = rule.SourceField;
            let condition: any = (rule.Condition as any).Value;
            // Remove " from escaping process
            condition = condition.replace(/"/g, "");
            source = source.replace(/"/g, "");
            return {
                ...acc,
                [condition as string]: source
            }
        }, []);
    }
    let listMappings: any = [];
    if (remoteProfile.Rules) {
        listMappings = remoteProfile.Rules.filter(rule => (rule.TargetField !== categoryColFromFile) && rule.Type !== "AdvancedAttributeRule").reduce((acc: any, rule) => {
            if(!rule.TargetField || !rule.SourceField) return acc;
            let source: any = rule.SourceField;
            let condition: any = (rule.Condition as any).Value;
            let mappedField = Object.entries(attributeMappings)
                .find(([key, value]) => value === rule.TargetField);
            if (mappedField === undefined) mappedField = ["", ""];
            // Remove " from escaping process
            source = source.replace(/"/g, "");
            condition = condition.replace(/"/g, "");
            return {
                ...acc,
                [mappedField[0]]: {
                    ...acc[mappedField[0]],
                    [source]: condition
                }
            }
        }, []);
    }
    let advancedAttributeRules: any = [];
    if (remoteProfile.Rules) {
        advancedAttributeRules = remoteProfile.Rules.filter(rule => rule.Type === "AdvancedAttributeRule");
        advancedAttributeRules = advancedAttributeRules.map((rule: any) => ({
            ...rule,
            Condition: unescapeFieldsInCondition(rule.Condition)
        }));
    }
    return {
        ...remoteProfile,
        Id: remoteProfile.Id,
        Shared: remoteProfile.Shared,
        Name: remoteProfile.Name || filterStringStored.Name,
        Description: remoteProfile.Description || filterStringStored.Description,
        DocumentType: remoteProfile.DocumentType || filterStringStored.DocumentType,
        Partner: partner || filterStringStored.Partner,
        Category: categoryColFromFile || filterStringStored.Category,
        CategoryMappings: categoryMappings || filterStringStored.CategoryMappings,
        AttributeMappings: attributeMappings || filterStringStored.AttributeMappings,
        ListMappings: listMappings || filterStringStored.ListMappings,
        AttributeMappingRules: advancedAttributeRules || []
    }
}

export const detectFileType = (file: File | null): string =>
    file && file.name && file.name.toLowerCase().endsWith(".xlsx") ? "xlsx" : "csv";

export const isListTypeAttribute = (attr: any): boolean =>
    !!attr && (attr.Type === "select" || attr.Type === "list");
